import { GoogleGenAI, GenerateContentResponse } from "@google/genai";
import { GEMINI_IMAGE_GEN_MODEL, GEMINI_VISION_MODEL } from '../constants';

/**
 * Generates an image using Gemini's Imagen model.
 * @param prompt - The text prompt describing the image to generate.
 * @returns A promise that resolves to the base64 encoded string of the generated image.
 */
export const generateImageWithGemini = async (prompt: string): Promise<string> => {
  // Always initialize a new GoogleGenAI instance with the API key from environment variables before the call
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

  try {
    const response = await ai.models.generateImages({
      model: GEMINI_IMAGE_GEN_MODEL,
      prompt: prompt,
      config: { numberOfImages: 1, outputMimeType: 'image/jpeg' },
    });

    if (
      response &&
      response.generatedImages &&
      Array.isArray(response.generatedImages) &&
      response.generatedImages.length > 0 &&
      response.generatedImages[0] &&
      response.generatedImages[0].image &&
      typeof response.generatedImages[0].image.imageBytes === 'string' &&
      response.generatedImages[0].image.imageBytes.length > 0 
    ) {
      return response.generatedImages[0].image.imageBytes;
    } else {
      console.error("Respuesta de generación de imagen de Gemini no válida.");
      throw new Error("No se recibieron datos de imagen válidos de Gemini.");
    }
  } catch (error) {
    console.error("Error durante la generación de imagen con Gemini:", error); 
    if (error instanceof Error) {
      if (error.message.includes("API key not valid") || error.message.includes("PERMISSION_DENIED")) {
        throw new Error("La clave API de Gemini no es válida o no tiene permisos.");
      }
      throw error;
    }
    throw new Error("Ocurrió un error desconocido durante la generación de imagen.");
  }
};

/**
 * Analyzes an image using Gemini's Vision model by referencing its URL.
 * @param userPrompt - The text prompt/question about the image.
 * @param imageUrl - The publicly accessible URL of the image to analyze.
 * @param mimeType - The MIME type of the image (e.g., "image/jpeg").
 * @returns A promise that resolves to the text analysis from Gemini.
 */
export const analyzeImageWithGemini = async (userPrompt: string, imageUrl: string, mimeType: string): Promise<string> => {
  // Always initialize a new GoogleGenAI instance with the API key from environment variables before the call
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

  try {
    const imageFetch = await fetch(imageUrl);
    if (!imageFetch.ok) throw new Error(`Error al obtener la imagen: ${imageFetch.status}`);
    const imageBlob = await imageFetch.blob();
    
    const effectiveMimeType = imageBlob.type && imageBlob.type.startsWith("image/") ? imageBlob.type : mimeType;

    const imageBytes = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(imageBlob);
    });

    const imagePart = {
        inlineData: {
            mimeType: effectiveMimeType,
            data: imageBytes,
        },
    };
    const textPart = { text: userPrompt };

    const response: GenerateContentResponse = await ai.models.generateContent({
        model: GEMINI_VISION_MODEL,
        contents: { parts: [textPart, imagePart] },
    });
    
    // Simplest way to get text output is accessing the .text property directly
    const textResponse = response.text;
    if (typeof textResponse !== 'string' || textResponse.trim() === "") {
        throw new Error("No se recibió una respuesta de texto válida del modelo de visión de Gemini.");
    }
    return textResponse.trim();

  } catch (error) {
    console.error("Error analizando imagen con Gemini:", error);
    if (error instanceof Error) {
      if (error.message.includes("API key not valid") || error.message.includes("PERMISSION_DENIED")) {
        throw new Error("La clave API de Gemini no es válida.");
      }
      throw error;
    }
    throw new Error("Error desconocido durante el análisis de imagen.");
  }
};